import { useEffect, useState } from 'react';
import { X, Loader2, Eye, RefreshCw, Download } from 'lucide-react';
import {
  DIRECTION_LABELS, isOptionVisible,
  type FlowDef, type FlowDirection, type FlowOptionDef, type FlowOptionValue, type FlowOptionValues, type WarehousePref,
} from '@/lib/interfacesCatalog';
import { interfacesService, describeOrdersRun } from '@/lib/interfacesService';
import { useErpWarehouses, WarehouseMultiField, WarehousePriorityField, type ErpWarehousesState } from './WarehouseFields';
import { FlowPreviewModal } from './FlowPreviewModal';
import { SkuListField } from './SkuListField';

interface FlowOptionsDrawerProps {
  companyId: string;
  flow: FlowDef;
  initialValues: FlowOptionValues;
  initialDirection: FlowDirection;
  readOnly: boolean;
  onSave: (values: FlowOptionValues, direction: FlowDirection) => Promise<void>;
  onClose: () => void;
}

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-gray-50';

function OptionField({ companyId, opt, value, disabled, warehouses, onChange }: {
  companyId: string;
  opt: FlowOptionDef;
  value: FlowOptionValue;
  disabled: boolean;
  warehouses: ErpWarehousesState;
  onChange: (v: FlowOptionValue) => void;
}) {
  if (opt.type === 'warehouse_priority') {
    return <WarehousePriorityField state={warehouses} value={Array.isArray(value) ? (value as WarehousePref[]) : []} disabled={disabled} onChange={onChange} />;
  }

  if (opt.type === 'warehouse_multi') {
    return <WarehouseMultiField state={warehouses} value={Array.isArray(value) ? (value as number[]) : []} disabled={disabled} onChange={onChange} />;
  }

  if (opt.type === 'sku_list') {
    return <SkuListField companyId={companyId} value={String(value ?? '')} disabled={disabled} onChange={onChange} />;
  }

  if (opt.type === 'choice') {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {opt.choices.map(c => {
          const selected = value === c.value;
          return (
            <button
              key={c.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(c.value)}
              className={`rounded-lg border-2 px-3 py-2 text-right transition-all duration-150 ${
                selected ? 'border-sky-500 bg-sky-50' : 'border-gray-200 bg-white hover:border-gray-300'
              } disabled:cursor-not-allowed`}
            >
              <span className={`block text-sm font-medium ${selected ? 'text-sky-800' : 'text-gray-800'}`}>{c.label}</span>
              {c.description && <span className="mt-0.5 block text-xs text-gray-500">{c.description}</span>}
            </button>
          );
        })}
      </div>
    );
  }

  if (opt.type === 'toggle') {
    const on = value === true;
    return (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        onClick={() => onChange(!on)}
        className={`relative h-6 w-11 rounded-full transition-colors ${on ? 'bg-sky-600' : 'bg-gray-300'} disabled:opacity-50`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? 'right-[22px]' : 'right-0.5'}`} />
      </button>
    );
  }

  if (opt.type === 'number') {
    return (
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={opt.min}
          value={Number(value)}
          disabled={disabled}
          onChange={e => onChange(Math.max(opt.min ?? -Infinity, Number(e.target.value) || 0))}
          className={`${inputClass} max-w-[140px]`}
        />
        {opt.suffix && <span className="text-sm text-gray-500">{opt.suffix}</span>}
      </div>
    );
  }

  return (
    <input
      type={opt.inputType ?? 'text'}
      dir={opt.inputType ? 'ltr' : undefined}
      value={String(value ?? '')}
      placeholder={opt.placeholder}
      disabled={disabled}
      maxLength={100}
      onChange={e => onChange(e.target.value)}
      className={inputClass}
    />
  );
}

const PREVIEW_KIND: Record<string, 'orders' | 'stock'> = { order_to_invoice: 'orders', stock_to_store: 'stock' };

export function FlowOptionsDrawer({ companyId, flow, initialValues, initialDirection, readOnly, onSave, onClose }: FlowOptionsDrawerProps) {
  const [values, setValues] = useState<FlowOptionValues>(initialValues);
  const [direction, setDirection] = useState<FlowDirection>(initialDirection);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<FlowOptionValues | null>(null);
  const [runState, setRunState] = useState<{ busy: boolean; message: string | null; error: boolean }>({ busy: false, message: null, error: false });
  const usesWarehouses = flow.options.some(o => o.type === 'warehouse_priority' || o.type === 'warehouse_multi');
  const warehouses = useErpWarehouses(companyId, usesWarehouses);
  const previewKind = PREVIEW_KIND[flow.key];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !preview) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, preview]);

  const runStockNow = async () => {
    setRunState({ busy: true, message: null, error: false });
    const r = await interfacesService.runStockNow(companyId);
    if (!r.ok) { setRunState({ busy: false, message: r.error, error: true }); return; }
    const s = r.data;
    const parts = [`עודכנו ${s.updated} מוצרים`, `${s.unchanged} ללא שינוי`];
    if (s.unmatched) parts.push(`${s.unmatched} מק"טים לא נמצאו בחנות`);
    if (s.skipped) parts.push(`${s.skipped} דולגו לפי הרשימה`);
    if (s.failed) parts.push(`${s.failed} נכשלו`);
    if (s.pending) parts.push(`${s.pending} יעודכנו בהרצה הבאה`);
    setRunState({ busy: false, message: parts.join(' · '), error: s.failed > 0 });
  };

  const runOrdersNow = async () => {
    setRunState({ busy: true, message: null, error: false });
    const r = await interfacesService.runOrdersNow(companyId);
    setRunState(r.ok
      ? { busy: false, message: describeOrdersRun(r.data), error: false }
      : { busy: false, message: r.error, error: true });
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(values, direction); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-start" dir="rtl">
      <div className="absolute inset-0 bg-black/40 animate-in fade-in duration-200" onClick={onClose} />
      <aside className="relative mr-auto flex h-full w-full max-w-xl flex-col bg-white shadow-2xl animate-in slide-in-from-left duration-300">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{flow.title}</h2>
            <p className="mt-0.5 text-sm text-gray-500">{flow.description}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="סגור">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          {flow.allowedDirections.length > 1 && (
            <div>
              <p className="mb-2 text-sm font-semibold text-gray-800">כיוון ההעברה</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {flow.allowedDirections.map(d => (
                  <button
                    key={d}
                    type="button"
                    disabled={readOnly}
                    onClick={() => setDirection(d)}
                    className={`rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all ${
                      direction === d ? 'border-sky-500 bg-sky-50 text-sky-800' : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {DIRECTION_LABELS[d]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {flow.options.filter(o => isOptionVisible(o, values)).map(opt => (
            <div key={opt.key} className={opt.type === 'toggle' ? 'flex items-center justify-between gap-4' : ''}>
              <div className={opt.type === 'toggle' ? '' : 'mb-2'}>
                <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
                {opt.help && <p className="mt-0.5 text-xs text-gray-500">{opt.help}</p>}
              </div>
              <OptionField
                companyId={companyId}
                opt={opt}
                value={values[opt.key] ?? opt.default}
                disabled={readOnly}
                warehouses={warehouses}
                onChange={v => setValues(prev => ({ ...prev, [opt.key]: v }))}
              />
            </div>
          ))}

          {previewKind && !readOnly && (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-800">בדיקה לפני הפעלה</p>
              <p className="mt-0.5 text-xs text-gray-500">
                {previewKind === 'orders'
                  ? 'רואים איך 10 ההזמנות האחרונות היו מתחלקות למחסנים ואילו מסמכים היו יוצאים, לפי ההגדרות שכאן.'
                  : 'רואים מה יתעדכן בחנות לפני שמעדכנים באמת.'}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setPreview(values)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                >
                  <Eye className="h-4 w-4" /> תצוגה מקדימה
                </button>
                {previewKind === 'stock' && (
                  <button
                    type="button"
                    onClick={runStockNow}
                    disabled={runState.busy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-sky-700 disabled:opacity-60"
                  >
                    <RefreshCw className={`h-4 w-4 ${runState.busy ? 'animate-spin' : ''}`} /> עדכן עכשיו
                  </button>
                )}
                {previewKind === 'orders' && (
                  <button
                    type="button"
                    onClick={runOrdersNow}
                    disabled={runState.busy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-sky-700 disabled:opacity-60"
                  >
                    {runState.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} משוך הזמנות עכשיו
                  </button>
                )}
              </div>
              {previewKind === 'stock' && <p className="mt-2 text-xs text-gray-500">"עדכן עכשיו" פועל לפי ההגדרות השמורות. שניתם משהו? שמרו קודם.</p>}
              {previewKind === 'orders' && <p className="mt-2 text-xs text-gray-500">המערכת בודקת הזמנות חדשות אוטומטית כל רבע שעה. "משוך הזמנות עכשיו" בודק מיד, לפי ההגדרות השמורות.</p>}
              {runState.message && (
                <p className={`mt-2 rounded-lg px-3 py-2 text-xs ${runState.error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>{runState.message}</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">
            {readOnly ? 'סגור' : 'ביטול'}
          </button>
          {!readOnly && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-5 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              שמור הגדרות
            </button>
          )}
        </div>
      </aside>
      {preview && previewKind && (
        <FlowPreviewModal kind={previewKind} companyId={companyId} options={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
